import type { MouseEvent } from 'react';
import { Suspense, useState } from 'react';

import { getZendeskHelpCentreUrl, ZENDESK_TABS } from '@skyscanner/zendesk-url';
import { BREAKPOINTS } from '@skyscanner-internal/backpack-web/bpk-component-breakpoint';
import BpkButton from '@skyscanner-internal/backpack-web/bpk-component-button';
import BpkIconHeart from '@skyscanner-internal/backpack-web/bpk-component-icon/lg/heart';
import BpkIconWorldEmea from '@skyscanner-internal/backpack-web/bpk-component-icon/lg/world--emea';
import BpkLink from '@skyscanner-internal/backpack-web/bpk-component-link';
import {
  BpkLargeSpinner,
  SPINNER_TYPES,
} from '@skyscanner-internal/backpack-web/bpk-component-spinner';
import BpkText, { TEXT_STYLES } from '@skyscanner-internal/backpack-web/bpk-component-text';
import BpkInlineLogo from '@skyscanner-internal/bpk-logos/src/BpkInlineLogo';
import {
  fetchCultureSelectorData,
  persistUserSessionData,
  useCultureSelectorData,
} from '@skyscanner-internal/global-components/culture-selector/helpers';
import { ROOT } from '@web-platform/shared-banana-constants/src/routes';
import { GlobalTabs } from '@web-platform/shared-banana-features-global-tabs';
import { useMediaQuery } from '@web-platform/shared-banana-utils-frontend/src/helpers/hooks/useMediaQuery/useMediaQuery';
import {
  CONFIG_SERVICE_KEYS,
  FEATURE_DECISION_KEYS,
} from '@web-platform/shared-universal-constants-config';
import { ANALYTICS_EVENTS } from '@web-platform/shared-universal-constants-tracking';
import {
  GlobalLoginForm,
  type GlobalLoginFormProps,
} from '@web-platform/shared-universal-features-global-login-form';
import {
  loggedInStatusChanged,
  loginVisibilityChanged,
} from '@web-platform/shared-universal-redux/src/actions/user';
import { getCurrentScreenName } from '@web-platform/shared-universal-redux/src/selectors/trustedFunnel';
import { getLogin } from '@web-platform/shared-universal-redux/src/state';
import { trackSmartMetric } from '@web-platform/shared-universal-tracking/src/trackExperiment';
import { trackInteractionEvent } from '@web-platform/shared-universal-tracking/src/trackInteractionEvent/trackInteractionEvent';
import { translate } from '@web-platform/shared-universal-translations';
import ErrorBoundary from '@web-platform/shared-universal-ui-error-boundary';
import {
  getConfigServiceValue,
  getFeatureDecision,
} from '@web-platform/shared-universal-utils-frontend/src/config';
import { lazyWithRetry } from '@web-platform/shared-universal-utils-frontend/src/helpers/lazyLoading';
import {
  logError,
  logOperationalEvent,
} from '@web-platform/shared-universal-utils-frontend/src/helpers/logger';
import { getAuthClient } from '@web-platform/shared-universal-utils-frontend/src/singletons/authClient';
import { getSubscriptionClient } from '@web-platform/shared-universal-utils-frontend/src/singletons/subscriptionClient';
import { getCulture } from '@web-platform/shared-universal-utils-frontend-localisation';
import classNames from 'classnames';
import { useDispatch, useSelector } from 'react-redux';
import { Link } from 'react-router-dom';

import LoginButton from './LoginButton';

import type { Source } from '@skyscanner-internal/global-components/login-form';
import type { LoginModalReferrer } from '@web-platform/shared-universal-types/src/redux/state/appInitial/user/login';

import STYLES from './GlobalHeader.module.scss';

type GlobalLoginFormWrapperProps = {
  consumer: 'banana';
  onClose: GlobalLoginFormProps['onClose'];
  screenName: string;
  source: Source;
};

const GlobalLoginFormWrapper = ({
  consumer,
  onClose,
  screenName,
  source,
}: GlobalLoginFormWrapperProps) => {
  const authClient = getAuthClient();
  const subscriptionClient = getSubscriptionClient();
  if (!subscriptionClient) return null;
  return (
    <GlobalLoginForm
      authClient={authClient}
      consumer={consumer}
      isOpen
      onClose={onClose}
      screenName={screenName}
      source={source}
      subscriptionClient={subscriptionClient}
    />
  );
};

export type Theme = 'light' | 'dark';

export const HEADER_LOGO_LINK_ID = 'header-logo-link';

type Props = {
  enableHeaderReducedVerticalSpacing?: boolean;
  theme: Theme;
  showTabs: boolean;
};

type ButtonProps = {
  onClick: () => void;
  buttonStyle: string;
};

type SaveToListLinkProps = {
  buttonStyle: string;
  onClick: () => void;
};

const LazyCultureSelectorModal = lazyWithRetry(
  () =>
    import(
      /* webpackPrefetch: true, webpackChunkName: "CultureSelectorModal" */
      '@web-platform/shared-banana-features-culture-selector'
    ),
);

const CultureSelectorButtonHeader = ({ buttonStyle, onClick }: ButtonProps) => (
  <button
    className={buttonStyle}
    onClick={(e: MouseEvent<HTMLElement>) => {
      e.preventDefault();
      onClick();
    }}
    aria-label={translate('aria_culture_selector_icon')}
    type="button"
  >
    <BpkIconWorldEmea className={STYLES.icon} />
  </button>
);

const SaveToListLink = ({ buttonStyle, onClick }: SaveToListLinkProps) => (
  <BpkButton
    className={buttonStyle}
    href="/profile/saved"
    onClick={() => {
      onClick();
    }}
    aria-label={translate('S2L_Saved_Flights_Web')}
  >
    <BpkIconHeart aria-hidden className={STYLES.icon} />
  </BpkButton>
);

const GlobalHeader = ({ enableHeaderReducedVerticalSpacing, showTabs, theme }: Props) => {
  const dispatch = useDispatch();
  const [isCultureSelectorOpen, setIsCultureSelectorOpen] = useState(false);
  const isSmallScreen = useMediaQuery(BREAKPOINTS.SMALL_TABLET);
  const enableAnonymousUtidPropagation = getFeatureDecision(
    FEATURE_DECISION_KEYS.ANONYMOUS_UTID_PROPAGATION_EXPLICIT_TLD_REDIRECT,
  );

  const { isOpen: isLoginModalOpen, referrer } = useSelector(getLogin);

  const { currency, locale, market } = getCulture();
  const includePreReleasedLocales = getConfigServiceValue(
    CONFIG_SERVICE_KEYS.OPS.SERVE_PRE_RELEASE_LOCALES,
  );

  const getLoginFormSource = (loginModalReferrer: LoginModalReferrer): Source => {
    if (loginModalReferrer === null) {
      return 'UNSET_SOURCE';
    }

    // Keyed on NonNullable<LoginModalReferrer> so TypeScript enforces exhaustive coverage —
    // adding a new referrer to LoginModalReferrer will cause a compile error here until it's mapped.
    const mapping: Record<NonNullable<LoginModalReferrer>, Source> = {
      SAVE_TO_LIST: 'S2L_ITINERARY',
      PRICE_ALERT: 'PRICE_ALERTS',
      HEADER_LOG_IN: 'HEADER',
      HEADER_SAVE_TO_LIST: 'S2L_HEADER_ICON',
      PRICING_OPTION_LOYALTY_CTA: 'OTHER',
      AUTO_PROMPT: 'AUTO_PROMPT',
      FLIGHT_ENTRY_LOYALTY_CTA: 'OTHER',
      LOYALTY_POC: 'OTHER',
      LOYALTY_POC_DAY_VIEW: 'OTHER',
      DROPS_CTA: 'OTHER',
    };

    return mapping[loginModalReferrer];
  };

  const {
    currencies,
    enMarkets,
    isFetchingCultureSelectorData,
    locales,
    markets,
    onCultureSelectorButtonClick,
  } = useCultureSelectorData(
    fetchCultureSelectorData,
    locale || 'en-GB',
    includePreReleasedLocales,
  );

  const onToggleClickCultureSelector = () => {
    if (!isCultureSelectorOpen) {
      headerCultureSelectorClicked();
    }
    onCultureSelectorButtonClick()
      .then(() => {
        setIsCultureSelectorOpen((prevState) => !prevState);
      })
      .catch((error) => {
        logError({
          component: 'CultureSelectorButton',
          error,
          message: `Error with CultureSelectorButtonClick`,
        });
      });
  };

  const currentScreenName = useSelector(getCurrentScreenName);

  const headerLogoClicked = () => {
    trackInteractionEvent({
      name: ANALYTICS_EVENTS.INTERACTION.GLOBAL_HEADER_LOGO_CLICKED.NAME,
      strings: {
        screenName: currentScreenName,
      },
    });
    logOperationalEvent({
      eventName: ANALYTICS_EVENTS.INTERACTION.GLOBAL_HEADER_LOGO_CLICKED.NAME,
    });
  };

  const headerCultureSelectorClicked = () => {
    trackInteractionEvent({
      name: ANALYTICS_EVENTS.INTERACTION.GLOBAL_HEADER_CULTURE_SELECTOR_CLICKED.NAME,
      strings: {
        screenName: currentScreenName,
      },
    });
    logOperationalEvent({
      eventName: ANALYTICS_EVENTS.INTERACTION.GLOBAL_HEADER_CULTURE_SELECTOR_CLICKED.NAME,
    });
  };

  const buttonStyle = theme === 'light' ? STYLES.buttonLight : STYLES.buttonDark;

  const handleSavedView = () => {
    trackInteractionEvent({
      name: ANALYTICS_EVENTS.INTERACTION.GLOBAL_HEADER_HEART_ICON_CLICKED.NAME,
      strings: {
        screenName: currentScreenName,
      },
    });
    logOperationalEvent({
      eventName: ANALYTICS_EVENTS.INTERACTION.GLOBAL_HEADER_HEART_ICON_CLICKED.NAME,
    });
  };

  return (
    <>
      <ErrorBoundary componentName="GlobalHeader" fallback={null}>
        <header
          id="header"
          className={classNames(theme === 'light' ? STYLES.headerLight : STYLES.headerDark)}
        >
          <Link
            id={HEADER_LOGO_LINK_ID}
            aria-label={translate('aria_header_logo')}
            onClick={headerLogoClicked}
            to={ROOT}
            className={STYLES.headerLogoLink}
          >
            <BpkInlineLogo className={theme === 'light' ? STYLES.logoLight : STYLES.logoDark} />
          </Link>
          <div className={STYLES.buttonGroup}>
            {!isSmallScreen && (
              <div>
                <BpkLink
                  className={STYLES.helpLink}
                  href={getZendeskHelpCentreUrl(ZENDESK_TABS.FLIGHTS, locale, market, currency)}
                  rel="noopener"
                  blank
                  alternate
                  implicit
                >
                  <BpkText textStyle={TEXT_STYLES.sm}>{translate('label_help_header')}</BpkText>
                </BpkLink>
              </div>
            )}

            <div>
              <Suspense fallback={<BpkLargeSpinner type={SPINNER_TYPES.light} />}>
                <CultureSelectorButtonHeader
                  buttonStyle={buttonStyle}
                  onClick={onToggleClickCultureSelector}
                />
              </Suspense>
            </div>

            <SaveToListLink buttonStyle={buttonStyle} onClick={handleSavedView} />
            <LoginButton
              buttonStyle={buttonStyle}
              iconStyle={STYLES.icon}
              screenName={currentScreenName}
            />
            {isLoginModalOpen && (
              <GlobalLoginFormWrapper
                consumer="banana"
                onClose={({ isLoggedIn }) => {
                  dispatch(loggedInStatusChanged(isLoggedIn));
                  dispatch(loginVisibilityChanged(false));
                  if (isLoggedIn && currentScreenName === 'HOME_PAGE') {
                    trackSmartMetric('homepage_login_form_success');
                  }
                }}
                screenName={currentScreenName}
                source={getLoginFormSource(referrer)}
              />
            )}
            {isCultureSelectorOpen && !isFetchingCultureSelectorData && (
              <LazyCultureSelectorModal
                currencies={currencies}
                markets={markets}
                enMarkets={enMarkets}
                locales={locales}
                isOpen={isCultureSelectorOpen}
                onClose={onToggleClickCultureSelector}
                persistUserSessionDataFn={
                  enableAnonymousUtidPropagation ? persistUserSessionData : undefined
                }
              />
            )}
          </div>
        </header>
      </ErrorBoundary>
      {showTabs && (
        <div className={classNames(STYLES.unifiedHeaderTabWrapper)}>
          <GlobalTabs
            enableHeaderReducedVerticalSpacing={enableHeaderReducedVerticalSpacing}
            screenName={currentScreenName}
          />
        </div>
      )}
    </>
  );
};

export { GlobalHeader };
